import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { RouteSkeleton } from "@/components/RouteSkeleton";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { SupportButton } from "@/components/SupportButton";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import { logAuthLatency, perfNow } from "@/lib/perf-logger";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  pendingComponent: RouteSkeleton,
  pendingMs: 0,
  beforeLoad: async ({ location }) => {
    const t0 = perfNow();
    // Use the restored browser session for the client-side route gate.
    // A second immediate /user revalidation after password sign-in can fail
    // transiently on Safari/mobile and incorrectly bounce users back to /auth.
    const { data: sessionRes } = await supabase.auth.getSession();
    const user = sessionRes.session?.user ?? null;
    logAuthLatency("getSession (gate)", t0);
    if (!user) throw redirect({ to: "/auth" });

    // Enforce email verification on every protected route except the
    // verify-email page itself (otherwise we'd redirect-loop).
    const emailConfirmed = Boolean(
      // Supabase returns either `email_confirmed_at` or `confirmed_at`.
      // Phone-only signups also count as confirmed.
      (user as { email_confirmed_at?: string | null }).email_confirmed_at ??
        (user as { confirmed_at?: string | null }).confirmed_at ??
        (user as { phone_confirmed_at?: string | null }).phone_confirmed_at,
    );
    if (!emailConfirmed && !location.pathname.startsWith("/verify-email")) {
      throw redirect({ to: "/verify-email" });
    }

    // Suspended-user guard: block access to all authenticated routes, sign out,
    // and bounce to the dedicated account-suspended screen with a clear reason.
    const { data: profileRow } = await supabase
      .from("profiles")
      .select("is_suspended, suspension_reason")
      .eq("id", user.id)
      .maybeSingle();
    const prof = profileRow as { is_suspended: boolean | null; suspension_reason: string | null } | null;
    if (prof?.is_suspended) {
      const reason = prof.suspension_reason ?? "Your account has been suspended.";
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      throw redirect({
        to: "/account-suspended",
        search: {
          reason: reason.slice(0, 240),
        } as never,
      });
    }

    // 2FA challenge gate: if the user has 2FA enrolled, require a per-session
    // proof before letting them into protected pages. The `mfa-verify` route
    // itself is exempt so we don't redirect-loop.
    if (!location.pathname.startsWith("/mfa-verify")) {
      const { data: mfaRow } = await supabase
        .from("user_mfa")
        .select("enrolled_at")
        .eq("user_id", user.id)
        .maybeSingle();
      const mfaEnrolled = Boolean((mfaRow as { enrolled_at: string | null } | null)?.enrolled_at);
      if (mfaEnrolled) {
        const okKey = `thr_mfa_ok_${user.id}`;
        const passed =
          typeof window !== "undefined" && window.sessionStorage.getItem(okKey) === "1";
        if (!passed) {
          throw redirect({
            to: "/mfa-verify",
            search: { redirect: location.pathname } as never,
          });
        }
      }
    }

    return { user };

  },
  component: () => (
    <>
      <AnnouncementBanner />
      <Outlet />
      <WhatsNewModal />
      <SupportButton />
    </>
  ),
});
